'use client'
import { useEffect, useMemo, useState } from 'react'
import { Loader2, Lock, ShieldCheck, Plus, ArrowRight, History, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { useClinicFormat } from '@/context/ClinicTimezoneContext'
import { useAuthStore } from '@/hooks/useAuthStore'
import {
  useClinicalHistory,
  useCreateClinicalHistory,
  useUpdateClinicalHistory,
  useSignClinicalHistory,
  useAddClinicalHistoryAddendum,
  useAddClinicalHistoryNote,
} from '@/hooks/useClinicalHistory'
import { cn } from '@/lib/utils'
import type { Patient } from '@/types'

// ── NOM-004 intake questionnaire structure (COFEPRIS) ────────────────────────
type IField = { key: string; label: string; type?: 'textarea'; }
type ISection = { key: string; title: string; femaleOnly?: boolean; cols?: 2 | 3; fields: IField[] }

const SECTIONS: ISection[] = [
  {
    key: 'familiares', title: 'Antecedentes heredofamiliares', cols: 2, fields: [
      { key: 'family_history', label: 'Enfermedades en padres, hermanos y abuelos (diabetes, hipertensión, cáncer, cardiopatías, psiquiátricas, otras)', type: 'textarea' },
      { key: 'family_deaths', label: 'Causa de muerte de familiares directos (si aplica)', type: 'textarea' },
    ],
  },
  {
    key: 'no_pat', title: 'Antecedentes personales no patológicos', cols: 3, fields: [
      { key: 'residence', label: 'Residencia y vivienda (servicios, hacinamiento)', type: 'textarea' },
      { key: 'diet', label: 'Alimentación (tipo de dieta)' },
      { key: 'meals_per_day', label: 'Comidas al día' },
      { key: 'hygiene', label: 'Higiene personal' },
      { key: 'physical_activity', label: 'Actividad física / ejercicio' },
      { key: 'smoking', label: 'Tabaquismo (sí/no, cantidad, años)' },
      { key: 'alcohol', label: 'Alcohol (sí/no, frecuencia, cantidad)' },
      { key: 'other_substances', label: 'Otras sustancias' },
      { key: 'education', label: 'Escolaridad' },
      { key: 'occupation', label: 'Ocupación actual y previa (riesgos de exposición)', type: 'textarea' },
    ],
  },
  {
    key: 'pat', title: 'Antecedentes personales patológicos', cols: 2, fields: [
      { key: 'childhood_illnesses', label: 'Enfermedades de la infancia' },
      { key: 'chronic_diseases', label: 'Crónico-degenerativas (diabetes, hipertensión, cardiopatías, etc.)', type: 'textarea' },
      { key: 'surgeries', label: 'Cirugías previas (tipo y fecha)', type: 'textarea' },
      { key: 'trauma', label: 'Traumatismos / lesiones' },
      { key: 'transfusions', label: 'Transfusiones' },
      { key: 'allergies', label: 'Alergias (medicamentos, alimentos, otras)', type: 'textarea' },
      { key: 'hospitalizations', label: 'Hospitalizaciones previas', type: 'textarea' },
      { key: 'vaccinations', label: 'Esquema de vacunación' },
      { key: 'current_medications', label: 'Medicamentos actuales', type: 'textarea' },
    ],
  },
  {
    key: 'gineco', title: 'Antecedentes gineco-obstétricos', femaleOnly: true, cols: 3, fields: [
      { key: 'menarche', label: 'Menarca' },
      { key: 'menstrual_cycle', label: 'Patrón del ciclo menstrual' },
      { key: 'sexual_activity_onset', label: 'Inicio de vida sexual' },
      { key: 'pregnancies', label: 'Gestas' },
      { key: 'deliveries', label: 'Partos' },
      { key: 'csections', label: 'Cesáreas' },
      { key: 'miscarriages', label: 'Abortos' },
      { key: 'family_planning', label: 'Método de planificación familiar' },
      { key: 'lmp', label: 'Fecha de última menstruación (FUM)' },
      { key: 'last_cytology', label: 'Última citología cervical' },
    ],
  },
  {
    key: 'pa', title: 'Padecimiento actual / motivo de primera consulta', cols: 2, fields: [
      { key: 'current_illness', label: 'Descripción cronológica (inicio, evolución, síntomas asociados)', type: 'textarea' },
    ],
  },
  {
    key: 'ros', title: 'Interrogatorio por aparatos y sistemas', cols: 2, fields: [
      { key: 'ros.cardiovascular', label: 'Cardiovascular' },
      { key: 'ros.respiratory', label: 'Respiratorio' },
      { key: 'ros.digestive', label: 'Digestivo' },
      { key: 'ros.genitourinary', label: 'Genitourinario' },
      { key: 'ros.endocrine', label: 'Endocrino' },
      { key: 'ros.musculoskeletal', label: 'Musculoesquelético' },
      { key: 'ros.neurological', label: 'Neurológico' },
      { key: 'ros.psychiatric', label: 'Psiquiátrico' },
    ],
  },
  {
    key: 'pe', title: 'Exploración física inicial', cols: 3, fields: [
      { key: 'vitals.bp', label: 'TA (mmHg)' },
      { key: 'vitals.hr', label: 'FC (lpm)' },
      { key: 'vitals.rr', label: 'FR (rpm)' },
      { key: 'vitals.temp', label: 'Temp (°C)' },
      { key: 'vitals.spo2', label: 'SpO₂ (%)' },
      { key: 'anthro.weight', label: 'Peso (kg)' },
      { key: 'anthro.height', label: 'Talla (cm)' },
      { key: 'anthro.bmi', label: 'IMC' },
      { key: 'exam.head_neck', label: 'Cabeza y cuello', type: 'textarea' },
      { key: 'exam.chest', label: 'Tórax', type: 'textarea' },
      { key: 'exam.cardiovascular', label: 'Cardiovascular', type: 'textarea' },
      { key: 'exam.abdomen', label: 'Abdomen', type: 'textarea' },
      { key: 'exam.extremities', label: 'Extremidades', type: 'textarea' },
      { key: 'exam.neurological', label: 'Neurológico', type: 'textarea' },
      { key: 'exam.skin', label: 'Piel y tegumentos', type: 'textarea' },
    ],
  },
]

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  SECTIONS.flatMap((s) => s.fields.map((f) => [f.key, f.label])),
)

type Content = Record<string, unknown>
const getAt = (obj: Content, path: string): string => {
  const v = path.split('.').reduce<unknown>((o, k) => (o == null ? undefined : (o as Content)[k]), obj)
  return v == null ? '' : String(v)
}
const setAt = (obj: Content, path: string, val: string): Content => {
  const keys = path.split('.')
  const next: Content = { ...obj }
  let cur = next
  keys.forEach((k, i) => {
    if (i === keys.length - 1) cur[k] = val
    else {
      cur[k] = { ...((cur[k] as Content) || {}) }
      cur = cur[k] as Content
    }
  })
  return next
}

export function ClinicalHistoryDocument({ patient }: { patient: Patient }) {
  const { formatDateTime } = useClinicFormat()
  const { toast } = useToast()
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'doctor' || role === 'nurse' || role === 'admin' || role === 'super_admin'

  const { data: history, isLoading } = useClinicalHistory(patient.id)
  const create = useCreateClinicalHistory()
  const update = useUpdateClinicalHistory()
  const sign = useSignClinicalHistory()
  const addAddendum = useAddClinicalHistoryAddendum()
  const addNote = useAddClinicalHistoryNote()

  const [content, setContent] = useState<Content>({})
  const [signOpen, setSignOpen] = useState(false)
  const [addendumOpen, setAddendumOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)

  const isSigned = history?.status === 'signed'
  const sections = useMemo(() => SECTIONS.filter((s) => !s.femaleOnly || patient.gender === 'female'), [patient.gender])

  // Hydrate the draft form from the server content (once loaded / after create).
  useEffect(() => {
    if (history && !isSigned) setContent((history.content as Content) || {})
  }, [history, isSigned])

  // Effective value of a field in the signed view: latest addendum wins.
  const effective = (key: string): string => {
    const last = [...(history?.addenda || [])].reverse().find((a) => a.field === key)
    if (last) return last.new_value
    return getAt((history?.content as Content) || {}, key)
  }

  // ── State: no history yet → create ──────────────────────────────────────────
  if (isLoading) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-ml-on-surface-variant/50" /></div>
  }

  if (!history) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-md text-sm italic text-ml-on-surface-variant/60">
          Este paciente aún no tiene una historia clínica. Cree el documento base (NOM-004) para iniciar el expediente; podrá guardarlo como borrador y firmarlo cuando esté completo.
        </p>
        {canEdit ? (
          <Button onClick={() => create.mutate({ patientId: patient.id }, {
            onSuccess: () => toast({ title: 'Historia clínica iniciada', description: 'Complete el cuestionario y firme cuando esté listo.' }),
            onError: () => toast({ title: 'No se pudo crear', variant: 'destructive' }),
          })} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Crear historia clínica
          </Button>
        ) : (
          <p className="text-xs text-ml-on-surface-variant/60">Solo personal médico autorizado puede crearla.</p>
        )}
      </div>
    )
  }

  const saveDraft = () =>
    update.mutate({ patientId: patient.id, content }, {
      onSuccess: () => toast({ title: 'Borrador guardado' }),
      onError: () => toast({ title: 'No se pudo guardar', variant: 'destructive' }),
    })

  const doSign = () =>
    sign.mutate({ patientId: patient.id }, {
      onSuccess: () => { setSignOpen(false); toast({ title: 'Historia clínica firmada y cerrada', description: 'El documento quedó bloqueado (solo lectura).' }) },
      onError: () => toast({ title: 'No se pudo firmar', variant: 'destructive' }),
    })

  return (
    <div className="space-y-6">
      {/* Status banner */}
      {isSigned ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-teal-200 bg-teal-50/70 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
            <div className="text-xs">
              <p className="font-semibold text-ml-on-surface">Historia clínica firmada por {history.attested_by_name || 'médico'}</p>
              <p className="text-ml-on-surface-variant/80">
                Céd. prof. {history.attested_by_cedula || 'no registrada'}
                {history.attested_at ? ` · ${formatDateTime(history.attested_at)}` : ''}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded bg-teal-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-800">
            <Lock className="h-3.5 w-3.5" /> Cerrada y firmada · solo lectura
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3">
          <p className="text-xs text-ml-on-surface-variant">
            <span className="font-semibold uppercase tracking-wider text-amber-700">Borrador</span>
            {' · '}editable hasta firmar. Al firmar, el documento se cierra permanentemente y solo podrá corregirse mediante notas de aclaración.
          </p>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={saveDraft} disabled={update.isPending}>
                {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar borrador
              </Button>
              <Button size="sm" onClick={() => setSignOpen(true)}>
                <Lock className="mr-2 h-4 w-4" />Firmar y cerrar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Intake sections */}
      <div className="space-y-7">
        {sections.map((section) => (
          <section key={section.key}>
            <h3 className="mb-3 border-b border-ml-outline-variant/30 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ml-on-surface">
              {section.title}
            </h3>
            <div className={cn('grid grid-cols-1 gap-x-6 gap-y-3', section.cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
              {section.fields.map((f) => {
                const isTextarea = f.type === 'textarea'
                return (
                  <div key={f.key} className={isTextarea ? (section.cols === 3 ? 'sm:col-span-3' : 'sm:col-span-2') : ''}>
                    <label className="mb-0.5 block text-[11px] font-normal text-ml-on-surface-variant">{f.label}</label>
                    {isSigned ? (
                      <p className={cn('whitespace-pre-wrap px-1.5 py-1 text-sm', effective(f.key) ? 'font-medium text-ml-on-surface' : 'italic text-ml-on-surface-variant/50')}>
                        {effective(f.key) || 'Sin información'}
                      </p>
                    ) : isTextarea ? (
                      <Textarea
                        value={getAt(content, f.key)}
                        onChange={(e) => setContent((c) => setAt(c, f.key, e.target.value))}
                        rows={2}
                        disabled={!canEdit}
                        placeholder="—"
                      />
                    ) : (
                      <Input
                        value={getAt(content, f.key)}
                        onChange={(e) => setContent((c) => setAt(c, f.key, e.target.value))}
                        disabled={!canEdit}
                        placeholder="—"
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      {/* ── Signed-only: clarifying addenda ─────────────────────────────────── */}
      {isSigned && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-ml-outline-variant/30 pb-1.5">
            <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ml-on-surface">
              <History className="h-3.5 w-3.5" /> Aclaraciones y correcciones
            </h3>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => setAddendumOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar aclaración
              </Button>
            )}
          </div>
          {(history.addenda || []).length === 0 ? (
            <p className="text-sm italic text-ml-on-surface-variant/50">
              Sin aclaraciones. El valor original firmado se conserva; cualquier corrección se registra aquí sin sobrescribirlo.
            </p>
          ) : (
            <ol className="space-y-3">
              {[...(history.addenda || [])].reverse().map((a, i) => (
                <li key={`${a.field}-${a.date}-${i}`} className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface p-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-ml-on-surface">{a.field_label || FIELD_LABELS[a.field] || a.field}</span>
                    <span className="text-[11px] text-ml-on-surface-variant/70">· {a.author_name} · {formatDateTime(a.date)}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded bg-rose-50 px-2 py-0.5 text-ml-on-surface-variant line-through">{a.previous_value || '—'}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-ml-on-surface-variant/50" />
                    <span className="rounded bg-teal-100 px-2 py-0.5 font-medium text-teal-900">{a.new_value}</span>
                  </div>
                  {a.note && <p className="mt-1.5 text-xs text-ml-on-surface-variant/80">{a.note}</p>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* ── Signed-only: follow-up notes ────────────────────────────────────── */}
      {isSigned && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-ml-outline-variant/30 pb-1.5">
            <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ml-on-surface">
              <FileText className="h-3.5 w-3.5" /> Notas de seguimiento
            </h3>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => setNoteOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar nota
              </Button>
            )}
          </div>
          {(history.notes || []).length === 0 ? (
            <p className="text-sm italic text-ml-on-surface-variant/50">
              Sin notas. Las notas de seguimiento se anexan sin modificar el documento original firmado.
            </p>
          ) : (
            <ol className="space-y-3">
              {[...(history.notes || [])].reverse().map((n) => (
                <li key={n.id} className="rounded-lg border-l-2 border-l-teal-400 bg-ml-surface p-3 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-ml-on-surface">{n.reason}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-teal-700">
                      <Lock className="h-3 w-3" /> firmada
                    </span>
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-ml-on-surface">{n.content}</p>
                  <p className="mt-1.5 text-[11px] text-ml-on-surface-variant/70">{n.author_name} · {formatDateTime(n.signed_at)}</p>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* Sign confirmation */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Firmar y cerrar la historia clínica</DialogTitle>
            <DialogDescription>
              El documento quedará firmado a su nombre y cédula profesional, con fecha y hora actuales, y se bloqueará como solo lectura. Después solo podrá corregirse mediante notas de aclaración. ¿Desea continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)} disabled={sign.isPending}>Cancelar</Button>
            <Button onClick={doSign} disabled={sign.isPending}>
              {sign.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Firmar y cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddendumDialog
        open={addendumOpen}
        onOpenChange={setAddendumOpen}
        sections={sections}
        currentValue={effective}
        pending={addAddendum.isPending}
        onSubmit={(payload) => addAddendum.mutate({ patientId: patient.id, data: payload }, {
          onSuccess: () => { setAddendumOpen(false); toast({ title: 'Aclaración registrada' }) },
          onError: () => toast({ title: 'No se pudo registrar', variant: 'destructive' }),
        })}
      />

      <NoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        pending={addNote.isPending}
        onSubmit={(payload) => addNote.mutate({ patientId: patient.id, data: payload }, {
          onSuccess: () => { setNoteOpen(false); toast({ title: 'Nota agregada' }) },
          onError: () => toast({ title: 'No se pudo agregar la nota', variant: 'destructive' }),
        })}
      />
    </div>
  )
}

function AddendumDialog({
  open, onOpenChange, sections, currentValue, pending, onSubmit,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  sections: ISection[]
  currentValue: (key: string) => string
  pending: boolean
  onSubmit: (payload: { section: string; field: string; field_label: string; previous_value: string | null; new_value: string; note: string | null }) => void
}) {
  const allFields = sections.flatMap((s) => s.fields.map((f) => ({ ...f, section: s.title })))
  const [field, setField] = useState(allFields[0]?.key ?? '')
  const [newValue, setNewValue] = useState('')
  const [note, setNote] = useState('')
  const meta = allFields.find((f) => f.key === field)

  useEffect(() => { if (open) { setField(allFields[0]?.key ?? ''); setNewValue(''); setNote('') } }, [open]) // eslint-disable-line

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar aclaración (NOM-004)</DialogTitle>
          <DialogDescription>El documento está firmado. El valor original no se modifica; se asienta una nota de aclaración con el valor anterior, el nuevo valor, su autoría y la fecha.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Campo a aclarar</label>
            <select value={field} onChange={(e) => setField(e.target.value)} className="w-full rounded-lg border border-ml-outline-variant/40 bg-ml-surface px-3 py-2 text-sm outline-none focus:border-ml-primary">
              {allFields.map((f) => <option key={f.key} value={f.key}>{f.section} — {f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Valor actual</label>
            <p className="rounded-lg bg-ml-surface-container-high px-3 py-2 text-sm text-ml-on-surface-variant">{currentValue(field) || '—'}</p>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Nuevo valor</label>
            <Textarea value={newValue} onChange={(e) => setNewValue(e.target.value)} rows={2} placeholder="Valor corregido o actualizado" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Motivo / nota (opcional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Ej. dato corregido tras revisión" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>Cancelar</Button>
          <Button
            disabled={pending || !newValue.trim()}
            onClick={() => onSubmit({ section: meta?.section ?? '', field, field_label: meta?.label ?? field, previous_value: currentValue(field) || null, new_value: newValue.trim(), note: note.trim() || null })}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Registrar aclaración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NoteDialog({
  open, onOpenChange, pending, onSubmit,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  pending: boolean
  onSubmit: (payload: { reason: string; content: string }) => void
}) {
  const [reason, setReason] = useState('')
  const [content, setContent] = useState('')
  useEffect(() => { if (open) { setReason(''); setContent('') } }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar nota de seguimiento</DialogTitle>
          <DialogDescription>La nota se anexa firmada y bloqueada, sin modificar el documento original. Queda registrada con su autoría y fecha.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Motivo de la nota</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej. Seguimiento, resultado de laboratorio…" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">Contenido</label>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="Evolución, hallazgos, indicaciones…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>Cancelar</Button>
          <Button disabled={pending || !reason.trim() || !content.trim()} onClick={() => onSubmit({ reason: reason.trim(), content: content.trim() })}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Firmar y agregar nota
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
